'use client'

import { useEffect } from 'react'

import { Notifications } from '@mantine/notifications'
import { QueryClientProvider } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { getCookie } from 'cookies-next/client'
import { useSearchParams } from 'next/navigation'

import * as config from '@/constants/config'
import { initializeFaroClient } from '@/lib/grafana/faro'
import { LocaleProvider } from '@/lib/locale'
import {
  DEFAULT_LOCALE,
  LOCALE_COOKIE_KEY,
  LOCALE_PARAM_KEY,
} from '@/lib/locale/locale.constants'
import { type Locale } from '@/lib/locale/locale.types'
import { getQueryClient } from '@/lib/query-client'
import { clearLegacyChartDefinitionCookies } from '@/utils/cookies'
import { extractSubdomain } from '@/utils/subdomain'

import AmplifyProvider from './amplify'
import MantineProvider from './mantine'

type ProvidersProps = Readonly<{
  children: React.ReactNode
}>

const Providers = (props: ProvidersProps) => {
  const { children } = props

  useEffect(() => {
    initializeFaroClient()
  }, [])

  // TEMPORARY: remove after legacy chartdef cookies are no longer present in the wild
  // (old embedded chart HTML wrote chartdef_* / lastChartDefinitionId; shrinks Cookie header)
  useEffect(() => {
    clearLegacyChartDefinitionCookies()
  }, [])

  const cookieLocale = getCookie(LOCALE_COOKIE_KEY)
  const searchParams = useSearchParams()
  const paramLocale = (searchParams.get(LOCALE_PARAM_KEY) as Locale) || undefined
  const locale = paramLocale ?? cookieLocale ?? DEFAULT_LOCALE

  const subdomain = extractSubdomain(globalThis.location?.hostname || '') || undefined
  const queryClient = getQueryClient()

  return (
    <QueryClientProvider client={queryClient}>
      <MantineProvider>
        <Notifications
          limit={config.NOTIFICATIONS_LIMIT}
          position={config.NOTIFICATIONS_POSITION}
          zIndex={config.NOTIFICATIONS_Z_INDEX}
        />
        <AmplifyProvider subdomain={subdomain}>
          <LocaleProvider initialLocale={locale}>{children}</LocaleProvider>
        </AmplifyProvider>
      </MantineProvider>
      <ReactQueryDevtools initialIsOpen={true} />
    </QueryClientProvider>
  )
}

export default Providers
